import type { UserSetting } from '@awork/_shared/models/user-setting.model'
import type { IPlannerSetting } from '@awork/features/planner/models/planner-settings-model'
import type {
  BoardGroupByOptions,
  DocsGroupByOptions,
  DocsSortByOptions,
  GroupByOptions,
  TimeTrackingGroupingOptions,
  TimeTrackingTimeFrameOptions
} from '@awork/_shared/models/group-by-options.model'
import type { SortByOption, SortingDirection } from '@awork/_shared/models/sort-by-option.model'
import type {
  AggregationOptions,
  TimelineFilters,
  TimeTrackingTimeFrame,
  ValidStandardTimeReportEntityType,
  ViewModesTimeTrackingGrouping
} from '@awork/features/time-tracking/models/types'
import type { DocumentSection } from '@awork/features/document/models/document.model'
import type { IPlannerTaskSidebar } from '@awork/features/planner/models/task-sidebar-settings.model'
import type { AdaptivePanelSettings } from './adaptive-panel.types'
import type { EntitySettings, GridColumnSettings } from '@awork/framework/state/settings.types'

export type DurationFormatType = 'decimal_days' | 'auto' | 'hours_minutes'

export type UserNameDisplayFormat = 'first_name' | 'full_name'

export type ListEntity = 'company' | 'project' | 'projectTimeline' | 'projectTask' | 'user' | 'myTasks' | 'planner'

export enum ProgressType {
  Tasks = 'tasks',
  Tracked = 'tracked'
}

interface ColumnSelection {
  company: string[]
  project: string[]
  projectTask: { [projectId: string]: string[]; default?: string[] }
  user: string[]
  myTasks: string[]
  planner: string[]
}

export interface ListColumns {
  smallList: ColumnSelection
  largeList: ColumnSelection
}

/**
 * Model for the global view persistence user setting
 */
export interface IViewPersistenceSetting {
  progressType?: ProgressType
  userList?: UserSetting[]
  companyList?: UserSetting[]
  taskView?: UserSetting[]
  projectDocs?: IProjectDocsSetting
  taskTimeline?: ITaskTimelineSetting
  privateTaskList?: IProjectTaskListSetting
  timeTrackingList?: UserSetting[]
  myTimeTracking?: UserSetting[]
  timeTrackingStandardTimeReport?: UserSetting[]
  dynamicDurationFormat?: DurationFormatType
  userNameDisplayFormat?: UserNameDisplayFormat
  framework?: IFrameworkViewSetting
  listColumns?: Partial<ListColumns>
  planner?: Partial<IPlannerSetting>
  projectTemplates?: IProjectTemplateViewSetting
  splitView?: UserSetting[]
  adaptivePanel?: AdaptivePanelSettings
  connectSettingsPage?: UserSetting[]
  templateProjectModal?: ITemplateProjectModalSetting
  plannedEffort?: IPlannedEffortSetting
  settingsPage?: ISettingsPageViewSetting
}

/**
 * View settings with type of UserSetting[]
 */
export const VIEW_PERSISTENCE_USER_SETTINGS: readonly (keyof IViewPersistenceSetting)[] = [
  'userList',
  'companyList',
  'taskView',
  'timeTrackingList',
  'timeTrackingStandardTimeReport',
  'myTimeTracking',
  'splitView',
  'connectSettingsPage'
]

/**
 * View settings with primitive value
 */
export const VIEW_PERSISTENCE_VALUE_SETTINGS: readonly (keyof IViewPersistenceSetting)[] = [
  'dynamicDurationFormat',
  'userNameDisplayFormat'
]

export type DarkModeSetting = 'on' | 'auto' | 'off'

export enum FloatingActionsPosition {
  Center = 'center',
  Right = 'right'
}

export interface IProjectDocsSetting {
  sortByOption: DocsSortByOptions
  sortByOptionDir: SortingDirection
  groupByOption: DocsGroupByOptions
  activeTab: 'gallery' | 'list'
}

export enum TemplateModalTab {
  ALL = 'all',
  CUSTOM = 'custom',
  BY_AWORK = 'byAwork'
}

export interface ITemplateProjectModalSetting {
  tab: TemplateModalTab
}

/**
 * Model for the planned effort selector user preferences
 */
export interface IPlannedEffortSetting {
  /**
   * Selected chart view mode in the planned effort hierarchy chart
   * - 'topDown': Available view
   * - 'bottomUp': Allocated view
   */
  chartViewMode?: 'topDown' | 'bottomUp'
}

export interface ISettingsPageViewSetting {
  sidebarExpanded?: boolean
  sidebarWidth?: number
  collapsedSectionIds?: string[]
}

/**
 * Model for the framework view persistance
 */
export interface IFrameworkViewSetting {
  menuPinned?: boolean
  menuWidth?: number
  menuCompact?: boolean
  hideTimers?: boolean
  darkMode?: DarkModeSetting
  companiesWithLogoSuggested?: string[]
  menuItemsExpandedState?: { group: string; expanded: boolean }[]
  recentlyVisitedProjects?: string[]
  pinnedProjects?: string[]
  keepNotificationOnNavigation?: boolean
  showSuggestedTimes?: boolean
  floatingActionsPosition?: FloatingActionsPosition
  aiChatExpanded?: boolean
  aiChatShowAsSidebar?: boolean
  aiChatSidebarPinned?: boolean
  aiChatSidebarWidth?: number
  showMenuFavoritesSection?: boolean
  showMenuExternalProjectsSection?: boolean
  showMenuMyProjectsSection?: boolean
  emojiRecents?: string[]
}

/**
 * Model for the time tracking view setting
 */
export interface ITimeTrackingViewSetting {
  selectedFilter?: string
  viewMode?: {
    value?: string
    grouping?: TimeTrackingGroupingOptions
  }
  datespan?: {
    value?: TimelineFilters
    aggregation?: AggregationOptions
  }
}

/**
 * Model for the task view setting
 */
export interface IProjectTaskListSetting {
  customFieldGrouping?: string
  projectId?: string
  showDone?: boolean
  showOldDoneTasks?: boolean
  lists?: { id: string; collapsed: boolean }[]
  users?: { id: string; collapsed: boolean }[]
  collapsedTasks?: CollapsedEntities
  collapsedGroups?: CollapsedEntities
  gridColumnSettings?: { id: string; width?: number; order?: number; visible?: boolean }[]
  gridView?: 'list' | 'table'
  adaptivePanel?: {
    splitWidth?: number
    overlayWidth?: number
  }
  viewMode?: string
  zoomLevel?: number
  sidebarExpanded?: boolean
  sortingOption?: SortByOption
  groupOption?: GroupByOptions
  boardGroupOption?: BoardGroupByOptions
  isListWideWidth?: boolean
  isGanttView?: boolean
  plannerTaskSidebar?: IPlannerTaskSidebar
}

/**
 * Model for the task list settings
 * This is a new model for the new Entity User Settings implementation
 */
export interface IProjectTaskListSettings extends EntitySettings {
  showDone?: boolean
  showOldDoneTasks?: boolean
  users?: { id: string; collapsed: boolean }[]
  collapsedGridRowIds?: string[]
  viewMode?: string
  zoomLevel?: number
  sidebarExpanded?: boolean
  sortingOption?: SortByOption
  groupOption?: GroupByOptions
  boardGroupOption?: BoardGroupByOptions
  isListWideWidth?: boolean
  isGanttView?: boolean
  plannerTaskSidebar?: IPlannerTaskSidebar
}

export interface IProjectViewSetting {
  projectId?: string
  viewMode?: string
}

export interface IProjectTemplateViewSetting {
  favoriteTemplates?: string[]
}

/**
 * Minimal object for storing entity ids as object keys with a isCollapsed boolean value.
 * This allows us to store a shorter string on the api and to easily convert to
 * a map using Object.fromEntries()
 */
export type CollapsedEntities = Record<string, boolean>

/**
 * Model for the time tracking tracked/planned per-project collapsed state
 */
export interface IProjectTimeTrackingTrackedPlannedSetting {
  projectId: string
  collapsedLists?: CollapsedEntities
  collapsedTasks?: CollapsedEntities
}

/**
 * Model for task view lists Settings
 */
export interface ITaskViewListsSetting {
  customFieldGrouping?: string
  id?: string
  gridColumns?: GridColumnSettings<string>[]
  groupOption?: GroupByOptions
  sortingOption?: SortByOption
  lastViewed?: boolean
  showDone?: boolean
  showScheduledTasks?: boolean
  showPlannedTasks?: boolean
  collapsedTasks?: CollapsedEntities
  collapsedLists?: CollapsedEntities
  collapsedGroups?: CollapsedEntities
  gridView?: 'list' | 'table'
  isListWideWidth?: boolean
  adaptivePanel?: {
    splitWidth?: number
    overlayWidth?: number
  }
  selectedDraftId?: string
}

/**
 * Model for the task timeline setting
 */
export interface ITaskTimelineSetting {
  sidebarExpanded?: boolean
  sidebarWidth?: number
  moveDependentTasks?: boolean
}

export type EntityTimesViewContext = 'my-day-list' | 'project-times' | 'company-times' | 'user-times'

export interface EntityTimesGridColumnSetting {
  id: string
  width?: number
  order?: number
  visible?: boolean
}

/**
 * Model for the entity times setting, used for standard time report
 * timeframe is deprecated and replaced by timeRange
 */
export interface IEntityTimesSettings {
  entityId: string
  entityType: ValidStandardTimeReportEntityType
  viewContext?: EntityTimesViewContext
  viewMode?: ViewModesTimeTrackingGrouping
  showUnplannedTasks?: boolean
  timeframe?: TimeTrackingTimeFrameOptions
  groupBy?: TimeTrackingGroupingOptions
  showDoneTasks?: boolean
  showOnlyBillableTimeTracked?: boolean
  timeRange?: TimeTrackingTimeFrame
  sortBy?: SortByOption<string>
  gridColumnSettings?: EntityTimesGridColumnSetting[]
}

export interface IDocumentSettings {
  sidebarExpanded?: boolean
  sidebarWidth?: number
  spacesExpanded?: boolean
  favoritesExpanded?: boolean
  sharedExpanded?: boolean
  privateExpanded?: boolean
  trashExpanded?: boolean
  expandedDocuments?: ExpandedDocument[]
  favoriteDocuments?: string[]
  wideDocuments?: string[]
  recentDocuments?: { id: string; lastOpened: Date }[]
}

export interface ExpandedDocument {
  id: string
  section: DocumentSection
}

export const MAX_RECENT_DOCUMENTS = 50
